import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";

import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import CommonTable from "../../../components/Common/TableContainer";
import { DEFAULT_PAGE_SIZE, pascalToCamel } from "helpers/GeneralHelper";
import { useBadFitResolutionReasonContext } from "libs/context/BadFitResolutionReasonContext";
import BadFitResolutionReasonAddEditModal from "pages/AllCommonModal/BadFitResolutionReasonAddEditModal/bad-fit-resolution-reason-add-edit-modal";
import { submitFilterData } from "model/BadFitResolutionReasonModel";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const BadFitResolutionReason = () => {
  const { permissionData } = usePermissionContext() || {};
  const {
    fetchBadFitResolutionReasonContextData,
    badFitResolutionReasonContextData,
  } = useBadFitResolutionReasonContext() || {};
  const { pageSize: settingsPageSize, settingsReady } =
    useCommonTableSettingsPageSize("Bad Fit Resolution Reason");
  const initialFilterFetchedRef = useRef(false);
  const [badFitResolutionReasonAddEditModalOpen, setBadFitResolutionReasonAddEditModalOpen] =
    useState(false);
  const [modal1, setModal1] = useState(false);
  const toggleViewModal = () => setModal1(!modal1);
  const [searchQuery, setSearchQuery] = useState("");
  const [badFitResolutionReasonData, setBadFitResolutionReasonData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemId, setItemId] = useState(null);
  const [tableMetaData, setTableMetadata] = useState();
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const filteredData = useMemo(() => badFitResolutionReasonData, [badFitResolutionReasonData]);

  const badFitResolutionReasonActionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.MasterBadFitResolutionReasonAccess),
      canEdit: Boolean(permissionData?.MasterBadFitResolutionReasonAccess),
    }),
    [permissionData]
  );

  const hasAnyBadFitResolutionReasonRowAction = useMemo(
    () => badFitResolutionReasonActionPermissions.canEdit,
    [badFitResolutionReasonActionPermissions]
  );

  const handleEdit = (row) => {
    setItemId(row?.id);
    setBadFitResolutionReasonAddEditModalOpen(true);
  };

  useEffect(() => {
    fetchBadFitResolutionReasonContextData();
  }, []);

  useEffect(() => {
    if (!settingsReady || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    getBadFitResolutionReasonList({ pageNumber: 1, pageSize });
  }, [settingsReady, settingsPageSize]);

  useEffect(() => {
    const valueReModified = badFitResolutionReasonContextData?.table?.columns?.map(
      (col) => ({
        Header: col?.title,
        accessor: pascalToCamel(col?.field),
      })
    );
    setTableMetadata(valueReModified);
  }, [badFitResolutionReasonContextData]);

  const getBadFitResolutionReasonList = async (payloadOverrides = {}) => {
    setLoading(true);
    const payload = {
      ...requestPayload,
      ...payloadOverrides,
    };
    const response = await submitFilterData(payload);
    const responseData = response?.data || {};
    const listData =
      responseData?.records ?? (Array.isArray(responseData) ? responseData : []);
    const valueReModified = (Array.isArray(listData) ? listData : []).map((item) => ({
      ...item,
    }));

    if (response) {
      setBadFitResolutionReasonData(valueReModified);
      setPaginationMeta({
        pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
        pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
        totalPages: Number(responseData?.totalPages) || 0,
        totalRecords: Number(responseData?.totalRecords) || valueReModified.length,
      });
      setRequestPayload(payload);
      setLoading(false);
    } else {
      setBadFitResolutionReasonData([]);
      setLoading(false);
    }
  };

  const handleSearchSubmit = async (value) => {
    const trimmedValue = value?.trim() || "";
    await getBadFitResolutionReasonList({
      ...requestPayload,
      pageNumber: 1,
      search: trimmedValue,
    });
  };

  const handlePageChange = async (page) => {
    await getBadFitResolutionReasonList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getBadFitResolutionReasonList({ pageNumber: 1, pageSize: rows });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="fw-bold mb-3">Bad Fit Resolution Reason</h5>
                {badFitResolutionReasonActionPermissions.canCreate ? (
                  <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                    <Button
                      color="primary"
                      onClick={() => {
                        setItemId(null);
                        setBadFitResolutionReasonAddEditModalOpen(true);
                      }}
                    >
                      + Create
                    </Button>
                  </Col>
                ) : null}
              </div>

              <hr />

              <Row className="align-items-center g-2 mb-4">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="border-0"
                      value={searchQuery}
                      onChange={(e) => {
                        const nextValue = e?.target?.value || "";
                        setSearchQuery(nextValue);
                        if (!nextValue.trim()) {
                          handleSearchSubmit("");
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearchSubmit(searchQuery?.trim());
                        }
                      }}
                    />

                    <Button
                      className="search-icon-button"
                      onClick={() => handleSearchSubmit(searchQuery?.trim())}
                    >
                      <i className="fa fa-search"></i>
                    </Button>
                  </div>
                </Col>
              </Row>

              {!loading && tableMetaData?.length ? (
                <CommonTable
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnyBadFitResolutionReasonRowAction}
                  onEdit={
                    badFitResolutionReasonActionPermissions.canEdit
                      ? handleEdit
                      : undefined
                  }
                  isEditAccess={badFitResolutionReasonActionPermissions.canEdit}
                  isCheckboxLeftSide={false}
                  isPagination={true}
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  pageName={"Bad Fit Resolution Reason"}
                />
              ) : (
                <Loaders />
              )}
            </CardBody>
          </Card>

          <BadFitResolutionReasonAddEditModal
            setBadFitResolutionReasonAddEditModalOpen={
              setBadFitResolutionReasonAddEditModalOpen
            }
            badFitResolutionReasonAddEditModalOpen={
              badFitResolutionReasonAddEditModalOpen
            }
            getBadFitResolutionReasonList={getBadFitResolutionReasonList}
            itemId={itemId}
            setItemId={setItemId}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

BadFitResolutionReason.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(BadFitResolutionReason);
